// Google 行事曆的狀態與操作（GDO-24～GDO-27）。登入狀態在伺服器的加密 cookie；這裡只記住
// 「曾經連結過」、選擇的行事曆與按過「不是」的建議（瀏覽器 localStorage），不寫入資料庫。
import type { IndexRow } from '@dayone/core/snapshot';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { readJson, writeJson } from '../storage/storage.ts';
import { CalendarApiError, type CalendarErrorCode, type CalendarInfo, calendarApi, type GameRef } from './api.ts';
import { type CalendarEvent, type SyncState, syncState, type UnboundEvent } from './status.ts';

export const CALENDAR_KEY = 'dayone:v1:calendar';
/** 預設同步的行事曆名稱（使用者原本整理遊戲發售日的行事曆，§11） */
export const DEFAULT_CALENDAR = 'Exciting Event';

interface Stored {
  signedIn?: boolean;
  calendarId?: string;
  /** 「不是」：`${eventId}:${gameId}` */
  dismissed?: string[];
}

export type AuthState = 'unknown' | 'unconfigured' | 'signed_out' | 'reauth' | 'signed_in';

export interface CalendarMessage {
  kind: 'error' | 'info';
  code: CalendarErrorCode | 'connected' | 'updated_all' | 'denied' | 'scope' | 'invalid_state';
}

export interface CalendarModel {
  auth: AuthState;
  email?: string;
  calendars: CalendarInfo[] | null;
  calendarId: string | null;
  /** 需要使用者從清單中選一個行事曆（找不到 Exciting Event） */
  needsCalendar: boolean;
  events: ReadonlyMap<string, CalendarEvent>;
  unbound: readonly UnboundEvent[];
  dismissed: ReadonlySet<string>;
  loading: boolean;
  /** 進行中的操作（遊戲 ID 或事件 ID） */
  busy: ReadonlySet<string>;
  progress: { done: number; total: number } | null;
  message: CalendarMessage | null;
  /** 「我的平台」 */
  platforms: string[];
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  stateOf: (row: IndexRow) => SyncState;
  connect: () => Promise<void>;
  logout: () => Promise<void>;
  chooseCalendar: (id: string) => void;
  refresh: () => Promise<void>;
  add: (row: IndexRow) => Promise<void>;
  update: (row: IndexRow, event: CalendarEvent) => Promise<'updated' | 'unchanged' | 'failed'>;
  remove: (event: CalendarEvent) => Promise<void>;
  bind: (eventId: string, row: IndexRow) => Promise<void>;
  unbind: (event: CalendarEvent) => Promise<void>;
  dismiss: (eventId: string, gameId: string) => void;
  updateAll: (items: { row: IndexRow; event: CalendarEvent }[]) => Promise<void>;
  clearMessage: () => void;
}

const LOGIN_ERRORS = new Set(['denied', 'scope', 'invalid_state']);

export function useCalendarModel({
  platforms,
  launch,
}: {
  platforms: string[];
  /** 開啟時網址上的參數（登入完成後的 connected=1 或 cal_error=原因），在網址正規化之前讀取 */
  launch: URLSearchParams;
}): CalendarModel {
  const stored = useRef<Stored>(readJson<Stored>(CALENDAR_KEY) ?? {});
  const save = useCallback((patch: Stored) => {
    stored.current = { ...stored.current, ...patch };
    writeJson(CALENDAR_KEY, stored.current);
  }, []);

  const [auth, setAuth] = useState<AuthState>('unknown');
  const [email, setEmail] = useState<string>();
  const [calendars, setCalendars] = useState<CalendarInfo[] | null>(null);
  const [calendarId, setCalendarId] = useState<string | null>(stored.current.calendarId ?? null);
  const [needsCalendar, setNeedsCalendar] = useState(false);
  const [events, setEvents] = useState<ReadonlyMap<string, CalendarEvent>>(new Map());
  const [unbound, setUnbound] = useState<readonly UnboundEvent[]>([]);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set(stored.current.dismissed ?? []));
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [message, setMessage] = useState<CalendarMessage | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const fail = useCallback((e: unknown) => {
    const code = e instanceof CalendarApiError ? e.code : 'unknown';
    if (code === 'reauth_required') setAuth('reauth');
    else if (code === 'signed_out') setAuth('signed_out');
    else if (code === 'not_configured') setAuth('unconfigured');
    if (code === 'calendar_not_found') {
      setCalendarId(null);
      setNeedsCalendar(true);
    }
    setMessage({ kind: 'error', code });
  }, []);

  const withBusy = async <T>(key: string, run: () => Promise<T>): Promise<T | undefined> => {
    setBusy((b) => new Set(b).add(key));
    try {
      return await run();
    } catch (e) {
      fail(e);
      return undefined;
    } finally {
      setBusy((b) => {
        const next = new Set(b);
        next.delete(key);
        return next;
      });
    }
  };

  const loadStatus = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const status = await calendarApi.status(id);
        setEvents(new Map(status.events.map((e) => [e.gameId, e])));
        setUnbound(status.unbound);
      } catch (e) {
        fail(e);
      } finally {
        setLoading(false);
      }
    },
    [fail],
  );

  /** 登入後：取得行事曆清單，選到記住的或名為 Exciting Event 的行事曆 */
  const afterSignIn = useCallback(async () => {
    const { calendars: list } = await calendarApi.calendars();
    setCalendars(list);
    const known = list.find((c) => c.id === stored.current.calendarId);
    const preferred = known ?? list.find((c) => c.name.trim().toLowerCase() === DEFAULT_CALENDAR.toLowerCase());
    if (preferred) {
      setCalendarId(preferred.id);
      save({ calendarId: preferred.id });
      setNeedsCalendar(false);
      await loadStatus(preferred.id);
    } else {
      setCalendarId(null);
      setNeedsCalendar(true);
      setPanelOpen(true);
    }
  }, [loadStatus, save]);

  // 開啟時：曾經連結過、或剛從 Google 登入回來，才向伺服器確認登入狀態（其他人不會多一次請求）
  // biome-ignore lint/correctness/useExhaustiveDependencies: 只在開啟時執行一次
  useEffect(() => {
    const connected = launch.get('connected') === '1';
    const error = launch.get('cal_error');
    if (error)
      setMessage({ kind: 'error', code: LOGIN_ERRORS.has(error) ? (error as CalendarMessage['code']) : 'unknown' });
    if (!connected && !stored.current.signedIn) {
      setAuth('signed_out');
      return;
    }
    (async () => {
      try {
        const me = await calendarApi.me();
        setEmail(me.email);
        setAuth('signed_in');
        save({ signedIn: true });
        if (connected) setMessage({ kind: 'info', code: 'connected' });
        await afterSignIn();
      } catch (e) {
        const code = e instanceof CalendarApiError ? e.code : 'unknown';
        if (code === 'signed_out') save({ signedIn: false });
        setAuth(code === 'reauth_required' ? 'reauth' : code === 'not_configured' ? 'unconfigured' : 'signed_out');
        if (code !== 'signed_out') fail(e);
      }
    })();
  }, []);

  const connect = useCallback(async () => {
    // 先確認伺服器已設定 Google（沒有設定時直接導過去只會看到錯誤頁）
    try {
      await calendarApi.me();
    } catch (e) {
      if (e instanceof CalendarApiError && e.code === 'not_configured') {
        setAuth('unconfigured');
        setMessage({ kind: 'error', code: 'not_configured' });
        return;
      }
    }
    window.location.assign(calendarApi.loginUrl(`${window.location.pathname}${window.location.search}`));
  }, []);

  const logout = useCallback(async () => {
    await calendarApi.logout().catch(() => undefined);
    save({ signedIn: false });
    setAuth('signed_out');
    setEmail(undefined);
    setCalendars(null);
    setEvents(new Map());
    setUnbound([]);
  }, [save]);

  const chooseCalendar = useCallback(
    (id: string) => {
      setCalendarId(id);
      setNeedsCalendar(false);
      save({ calendarId: id });
      void loadStatus(id);
    },
    [loadStatus, save],
  );

  const refresh = useCallback(async () => {
    if (calendarId) await loadStatus(calendarId);
  }, [calendarId, loadStatus]);

  const ref = (row: IndexRow): GameRef => ({ gameId: row.id, x: row.x, platforms });
  const put = (e: CalendarEvent) =>
    setEvents((m) => {
      const next = new Map(m);
      next.set(e.gameId, e);
      return next;
    });
  const drop = (gameId: string) =>
    setEvents((m) => {
      const next = new Map(m);
      next.delete(gameId);
      return next;
    });

  const add = async (row: IndexRow) => {
    if (!calendarId) return;
    const saved = await withBusy(row.id, () => calendarApi.add(calendarId, ref(row)));
    if (saved) put(saved);
  };

  const update = async (row: IndexRow, event: CalendarEvent) => {
    if (!calendarId) return 'failed' as const;
    const saved = await withBusy(row.id, () => calendarApi.update(calendarId, event.eventId, ref(row)));
    if (!saved) return 'failed' as const;
    put(saved);
    return saved.unchanged ? ('unchanged' as const) : ('updated' as const);
  };

  const remove = async (event: CalendarEvent) => {
    if (!calendarId) return;
    const ok = await withBusy(event.gameId, () => calendarApi.remove(calendarId, event.eventId).then(() => true));
    if (ok) drop(event.gameId);
  };

  const bind = async (eventId: string, row: IndexRow) => {
    if (!calendarId) return;
    const saved = await withBusy(eventId, () => calendarApi.bind(calendarId, eventId, ref(row)));
    if (!saved) return;
    put(saved);
    setUnbound((list) => list.filter((e) => e.eventId !== eventId));
  };

  const unbind = async (event: CalendarEvent) => {
    if (!calendarId) return;
    const ok = await withBusy(event.gameId, () => calendarApi.unbind(calendarId, event.eventId).then(() => true));
    if (!ok) return;
    drop(event.gameId);
    // 使用者自己的事件解除後回到「其他事件」；Day One 建立的事件解除後只是一般事件
    setUnbound((list) => [
      ...list,
      { eventId: event.eventId, summary: '', date: event.date, htmlLink: event.htmlLink },
    ]);
  };

  const dismiss = (eventId: string, gameId: string) => {
    const next = new Set(dismissed).add(`${eventId}:${gameId}`);
    setDismissed(next);
    save({ dismissed: [...next].slice(-500) });
  };

  /** 全部更新：依序執行，每 10 筆暫停一下，避免撞到 Google 的配額（§11.3） */
  const updateAll = async (items: { row: IndexRow; event: CalendarEvent }[]) => {
    setProgress({ done: 0, total: items.length });
    let failed = false;
    for (const [i, item] of items.entries()) {
      if (i > 0 && i % 10 === 0) await new Promise((r) => setTimeout(r, 1000));
      const result = await update(item.row, item.event);
      if (result === 'failed') {
        failed = true;
        break;
      }
      setProgress({ done: i + 1, total: items.length });
    }
    setProgress(null);
    if (!failed) setMessage({ kind: 'info', code: 'updated_all' });
  };

  const stateOf = useCallback((row: IndexRow) => syncState(row, events.get(row.id), platforms), [events, platforms]);

  return {
    auth,
    email,
    calendars,
    calendarId,
    needsCalendar,
    events,
    unbound,
    dismissed,
    loading,
    busy,
    progress,
    message,
    platforms,
    panelOpen,
    openPanel: () => setPanelOpen(true),
    closePanel: () => setPanelOpen(false),
    stateOf,
    connect,
    logout,
    chooseCalendar,
    refresh,
    add,
    update,
    remove,
    bind,
    unbind,
    dismiss,
    updateAll,
    clearMessage: () => setMessage(null),
  };
}

export const CalendarContext = createContext<CalendarModel | null>(null);

/** 卡片與詳情取得行事曆狀態；沒有 Provider（例如單獨測試元件）時回傳 null */
export function useCalendar(): CalendarModel | null {
  return useContext(CalendarContext);
}

/** 目前所有「日期已變動」的遊戲（頂部提示與全部更新用） */
export function useDrifts(model: CalendarModel | null, rows: readonly IndexRow[]) {
  return useMemo(() => {
    if (model?.auth !== 'signed_in') return [];
    const byId = new Map(rows.map((r) => [r.id, r]));
    const out: { row: IndexRow; event: CalendarEvent; from: string; to: string }[] = [];
    for (const event of model.events.values()) {
      const row = byId.get(event.gameId);
      if (!row) continue;
      const s = model.stateOf(row);
      if (s.kind === 'drift') out.push({ row, event, from: s.from, to: s.to });
    }
    return out;
  }, [model, rows]);
}
